/* JAPANIME.IT — Phase 46 clean product page behaviour. */
(() => {
  const ROOT_SELECTOR = '.jp-product-v2';
  const STATE_CLASSES = [
    'jp-product-v2--available',
    'jp-product-v2--preorder',
    'jp-product-v2--coming-soon',
    'jp-product-v2--sold-out',
  ];

  const stickyOffset = () => {
    const japanimeHeader = document.querySelector('japanime-header.jp-header-shell--sticky');
    if (japanimeHeader && window.matchMedia('(max-width: 900px)').matches) {
      return japanimeHeader.closest('.jp-header-section')?.offsetHeight || japanimeHeader.offsetHeight || 0;
    }

    const selectors = [
      '.jp-header-shell--sticky',
      '.shopify-section-header-sticky',
      '.section-header.shopify-section-header-sticky',
      'header.header',
    ];
    return selectors.reduce((height, selector) => {
      const element = document.querySelector(selector);
      if (!element) return height;
      const rect = element.getBoundingClientRect();
      return Math.max(height, rect.bottom > 0 ? rect.height : 0);
    }, 0);
  };

  const scrollGalleryIntoView = (gallery, image) => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const run = (behavior = reducedMotion ? 'auto' : 'smooth') => {
      const top = window.scrollY + gallery.getBoundingClientRect().top - stickyOffset() - 16;
      window.scrollTo({
        top: Math.max(0, top),
        behavior,
      });
    };

    requestAnimationFrame(() => {
      run();
      if (image && !image.complete) image.addEventListener('load', () => run(), { once: true });
      if (window.matchMedia('(max-width: 900px)').matches) {
        window.setTimeout(() => run(reducedMotion ? 'auto' : 'smooth'), 180);
        window.setTimeout(() => run('auto'), 420);
      }
    });
  };

  const initializeGallery = (root) => {
    const gallery = root.querySelector('[data-jp-gallery]');
    if (!gallery || gallery.dataset.jpGalleryReady === 'true') return;
    gallery.dataset.jpGalleryReady = 'true';

    const slides = [...gallery.querySelectorAll('[data-jp-gallery-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-jp-gallery-thumb]')];
    const stage = gallery.querySelector('[data-jp-gallery-stage]');
    const current = gallery.querySelector('[data-jp-gallery-current]');
    const dialog = root.querySelector('[data-jp-gallery-dialog]');
    const dialogImage = dialog?.querySelector('[data-jp-gallery-dialog-image]');
    const dialogStage = dialog?.querySelector('[data-jp-lightbox-stage]');
    const dialogPrevious = dialog?.querySelector('[data-jp-lightbox-prev]');
    const dialogNext = dialog?.querySelector('[data-jp-lightbox-next]');
    const dialogCurrent = dialog?.querySelector('[data-jp-lightbox-current]');
    const dialogTotal = dialog?.querySelector('[data-jp-lightbox-total]');
    const lightboxItems = slides
      .map((slide, slideIndex) => ({ slideIndex, button: slide.querySelector('[data-jp-gallery-open]') }))
      .filter((item) => item.button);
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let lightboxIndex = Math.max(0, lightboxItems.findIndex((item) => item.slideIndex === activeIndex));
    let suppressOpenUntil = 0;

    const activate = (requestedIndex, options = {}) => {
      if (!slides.length) return;
      const index = (requestedIndex + slides.length) % slides.length;
      activeIndex = index;

      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        if (active && options.syncThumb !== false) {
          thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      });

      if (current) current.textContent = String(index + 1);
      const activeImage = slides[index]?.querySelector('.jp-product-v2__main-image');
      if (options.scroll !== false) scrollGalleryIntoView(gallery, activeImage);
    };

    const activateMedia = (mediaId, options = {}) => {
      const index = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      if (index >= 0) activate(index, options);
    };

    gallery.jpActivateMedia = activateMedia;
    gallery.querySelector('[data-jp-gallery-prev]')?.addEventListener('click', () => activate(activeIndex - 1));
    gallery.querySelector('[data-jp-gallery-next]')?.addEventListener('click', () => activate(activeIndex + 1));

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        activate(index);
        gallery.focus({ preventScroll: true });
      });
    });

    gallery.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.target.matches('input, select, textarea')) return;
      event.preventDefault();
      activate(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });

    let touchStartX = 0;
    let touchStartY = 0;
    stage?.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
    }, { passive: true });
    stage?.addEventListener('touchend', (event) => {
      if (!event.changedTouches.length || slides.length < 2) return;
      const deltaX = event.changedTouches[0].clientX - touchStartX;
      const deltaY = event.changedTouches[0].clientY - touchStartY;
      if (Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
      suppressOpenUntil = Date.now() + 420;
      activate(activeIndex + (deltaX < 0 ? 1 : -1), { scroll: false });
    }, { passive: true });

    const showLightboxItem = (requestedIndex) => {
      if (!dialogImage || !lightboxItems.length) return;
      lightboxIndex = (requestedIndex + lightboxItems.length) % lightboxItems.length;
      const item = lightboxItems[lightboxIndex];
      const sourceImage = item.button.querySelector('img');
      dialogImage.src = item.button.dataset.fullImage || sourceImage?.currentSrc || sourceImage?.src || '';
      dialogImage.alt = sourceImage?.alt || '';
      if (dialogCurrent) dialogCurrent.textContent = String(lightboxIndex + 1);
      if (dialogTotal) dialogTotal.textContent = String(lightboxItems.length);
      activate(item.slideIndex, { scroll: false, syncThumb: false });
    };

    const moveLightbox = (direction) => showLightboxItem(lightboxIndex + direction);

    [dialogPrevious, dialogNext].forEach((button) => {
      if (button) button.hidden = lightboxItems.length < 2;
    });
    const dialogCount = dialog?.querySelector('.jp-product-v2__lightbox-count');
    if (dialogCount) dialogCount.hidden = lightboxItems.length < 2;

    gallery.querySelectorAll('[data-jp-gallery-open]').forEach((button) => {
      button.addEventListener('click', () => {
        if (Date.now() < suppressOpenUntil) return;
        if (!dialog || !dialogImage || typeof dialog.showModal !== 'function') return;
        const requestedIndex = lightboxItems.findIndex((item) => item.button === button);
        showLightboxItem(Math.max(0, requestedIndex));
        dialog.showModal();
      });
    });

    dialogPrevious?.addEventListener('click', () => moveLightbox(-1));
    dialogNext?.addEventListener('click', () => moveLightbox(1));

    let dragStartX = 0;
    let dragStartY = 0;
    let dragPointerId = null;
    dialogStage?.addEventListener('pointerdown', (event) => {
      if (event.target.closest('button')) return;
      dragPointerId = event.pointerId;
      dragStartX = event.clientX;
      dragStartY = event.clientY;
      dialogStage.classList.add('is-dragging');
      dialogStage.setPointerCapture?.(event.pointerId);
    });

    const finishLightboxDrag = (event) => {
      if (dragPointerId !== event.pointerId) return;
      const deltaX = event.clientX - dragStartX;
      const deltaY = event.clientY - dragStartY;
      dragPointerId = null;
      dialogStage?.classList.remove('is-dragging');
      if (lightboxItems.length > 1 && Math.abs(deltaX) >= 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.1) {
        moveLightbox(deltaX < 0 ? 1 : -1);
      }
    };
    dialogStage?.addEventListener('pointerup', finishLightboxDrag);
    dialogStage?.addEventListener('pointercancel', finishLightboxDrag);
    dialogImage?.addEventListener('dragstart', (event) => event.preventDefault());

    dialog?.querySelector('[data-jp-gallery-close]')?.addEventListener('click', () => dialog.close());
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        moveLightbox(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        moveLightbox(1);
      }
    });

    activate(activeIndex, { scroll: false });
  };

  const reviewHasContent = (block) => {
    const badge = block.querySelector('.jdgm-prev-badge, [class*="jdgm-prev-badge"]');
    if (!badge) return false;
    const countValue = badge.getAttribute('data-number-of-reviews');
    const scoreValue = badge.getAttribute('data-average-rating') || badge.querySelector('[data-score]')?.getAttribute('data-score');
    const text = (badge.textContent || '').replace(/\s+/g, ' ').trim();
    const textCount = text.match(/\b(\d+)\s*(?:review|recension)/i);
    const count = countValue !== null && countValue !== '' ? Number(countValue) : Number(textCount?.[1]);
    const score = Number(scoreValue);
    return (Number.isFinite(count) && count > 0) || (Number.isFinite(score) && score > 0);
  };

  const enhanceReviewPreview = (root) => {
    root.querySelectorAll('[data-jp-review-preview]').forEach((block) => {
      block.classList.toggle('is-visible', reviewHasContent(block));
    });
  };

  const enhanceWishlist = (root) => {
    root.querySelectorAll('[data-jp-wishlist] :is(button, a, [role="button"])').forEach((control) => {
      control.classList.add('jp-product-v2__wishlist-control');
    });
  };

  const styleDescriptionSize = (root) => {
    const description = root.querySelector('.jp-product-v2__description .product__description');
    const firstLine = description?.firstElementChild;
    if (firstLine && /^\s*(?:size|dimensioni)\s*:/i.test(firstLine.textContent || '')) {
      firstLine.classList.add('jp-product-v2__authored-dimensions');
      [firstLine, ...firstLine.querySelectorAll('*')].forEach((element) => {
        element.style?.removeProperty('color');
        element.style?.removeProperty('font-size');
      });
    }
  };

  const isolateOneTimePurchase = (root) => {
    const forms = [...root.querySelectorAll('form[data-type="add-to-cart-form"], form[action*="/cart/add"]')];
    const formIds = new Set(forms.map((form) => form.id).filter(Boolean));
    const detachSellingPlanFields = () => {
      document.querySelectorAll('[name="selling_plan"]').forEach((field) => {
        const associatedForm = field.getAttribute('form');
        if (!root.contains(field) && !(associatedForm && formIds.has(associatedForm))) return;
        field.disabled = true;
        field.hidden = true;
        field.setAttribute('aria-hidden', 'true');
        field.dataset.jpOneTimePurchaseOnly = 'true';
      });
    };

    const protectNativeAction = () => {
      if (!root.matches('.jp-product-v2--preorder, .jp-product-v2--coming-soon')) return;
      const button = root.querySelector('.product-form__submit[name="add"]');
      if (!button) return;
      button.classList.remove('timesact-button', 'jp-timesact-action', 'jp-legacy-timesact-ui');
      button.removeAttribute('aria-hidden');
      button.removeAttribute('tabindex');
      button.hidden = false;
      button.style.removeProperty('display');
      button.style.removeProperty('visibility');
      if (button.dataset.jpNativePurchasable === 'true') {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
    };

    forms.forEach((form) => {
      if (form.dataset.jpOneTimePurchaseReady === 'true') return;
      form.dataset.jpOneTimePurchaseReady = 'true';
      form.addEventListener('formdata', (event) => event.formData.delete('selling_plan'));
      form.addEventListener('submit', detachSellingPlanFields, true);
    });

    if (root.dataset.jpOneTimePurchaseGuard !== 'true') {
      root.dataset.jpOneTimePurchaseGuard = 'true';
      root.addEventListener('pointerdown', detachSellingPlanFields, true);
      root.addEventListener('click', detachSellingPlanFields, true);
    }

    detachSellingPlanFields();
    protectNativeAction();
  };

  const enhanceRoot = (root) => {
    initializeGallery(root);
    enhanceReviewPreview(root);
    enhanceWishlist(root);
    styleDescriptionSize(root);
    isolateOneTimePurchase(root);

    if (root.dataset.jpProductV2Observed === 'true') return;
    root.dataset.jpProductV2Observed = 'true';
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        enhanceReviewPreview(root);
        enhanceWishlist(root);
        styleDescriptionSize(root);
        isolateOneTimePurchase(root);
      });
    }).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-number-of-reviews', 'data-average-rating', 'data-score', 'class', 'disabled', 'hidden', 'style', 'value'],
    });
  };

  const initialize = () => document.querySelectorAll(ROOT_SELECTOR).forEach(enhanceRoot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
      document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
        if (String(root.dataset.section) !== String(data?.sectionId)) return;
        const source = data?.html?.querySelector?.(`${ROOT_SELECTOR}[data-section="${root.dataset.section}"]`);
        if (source) {
          const stateClass = STATE_CLASSES.find((className) => source.classList.contains(className));
          if (stateClass) {
            root.classList.remove(...STATE_CLASSES);
            root.classList.add(stateClass);
          }
          const stateSource = source.querySelector('[data-jp-product-state]');
          const stateTarget = root.querySelector('[data-jp-product-state]');
          if (stateSource && stateTarget) {
            stateTarget.innerHTML = stateSource.innerHTML;
            stateTarget.dataset.productState = stateSource.dataset.productState;
          }
          const saleBadgeSource = source.querySelector('[data-jp-sale-badge]');
          const saleBadgeTarget = root.querySelector('[data-jp-sale-badge]');
          if (saleBadgeSource && saleBadgeTarget) {
            saleBadgeTarget.innerHTML = saleBadgeSource.innerHTML;
            saleBadgeTarget.hidden = saleBadgeSource.hidden;
          }
          const badgesSource = source.querySelector('[data-jp-variant-badges]');
          const badgesTarget = root.querySelector('[data-jp-variant-badges]');
          if (badgesSource && badgesTarget) {
            badgesTarget.replaceChildren(...[...badgesSource.children].map((badge) => badge.cloneNode(true)));
            badgesTarget.hidden = badgesSource.hidden;
            root.classList.toggle('jp-product-v2--native-badges', !badgesSource.hidden);
          }
          const sourceButton = source.querySelector('.product-form__submit[name="add"]');
          const targetButton = root.querySelector('.product-form__submit[name="add"]');
          const sourceLabel = sourceButton?.querySelector(':scope > span:not(.loading__spinner)');
          const targetLabel = targetButton?.querySelector(':scope > span:not(.loading__spinner)');
          if (sourceButton && targetButton) {
            targetButton.dataset.jpNativePurchasable = sourceButton.dataset.jpNativePurchasable || 'false';
          }
          if (sourceLabel && targetLabel) targetLabel.innerHTML = sourceLabel.innerHTML;
        }
        if (data?.variant?.featured_media?.id) {
          root.querySelector('[data-jp-gallery]')?.jpActivateMedia?.(data.variant.featured_media.id);
        }
      });
    });
  }
})();
